"use client";

import { useEffect, useMemo, useState } from "react";

import { BlogAccordionList } from "./blog-accordion-list";
import { BlogFilterRail, useSelectedCategories } from "./blog-filter-rail";
import type { BlogCategoryFilter, BlogListingLabels, BlogPost } from "./data";

// Default list size; "Load more" reveals the next batch (or whatever
// remains) — Figma content note on 3466:18906.
const POSTS_PER_BATCH = 20;

interface BlogContentColumnProps {
  posts: BlogPost[];
  filters: BlogCategoryFilter[];
  totalCount: number;
  labels: BlogListingLabels;
}

/**
 * Blog posts section (Figma 3466:18906) — sits flush under the hero band.
 * The page rails continue from the hero down to plus connectors at the
 * bottom corners; the filter rail pins under the nav in the left column and
 * scrolls away with the listing. The rows column carries a full-height left
 * divider.
 */
export function BlogContentColumn({
  posts,
  filters,
  totalCount,
  labels,
}: BlogContentColumnProps) {
  const selectedCategories = useSelectedCategories();
  const [visibleCount, setVisibleCount] = useState(POSTS_PER_BATCH);

  const filteredPosts = useMemo(() => {
    if (!selectedCategories.length) return posts;
    return posts.filter((post) =>
      post.categories.some((category) => selectedCategories.includes(category))
    );
  }, [posts, selectedCategories]);

  // Changing filters resets the visible window.
  useEffect(() => {
    setVisibleCount(POSTS_PER_BATCH);
  }, [selectedCategories]);

  const visiblePosts = filteredPosts.slice(0, visibleCount);
  const hasMore = filteredPosts.length > visibleCount;

  return (
    <div className="bg-grey-50 border-grey-100 relative lg:border-t lg:border-b">
      <div className="relative mx-auto max-w-[1440px]">
        <div className="bg-grey-0 border-grey-100 relative grid grid-cols-1 lg:mx-20 lg:grid-cols-[217px_minmax(0,1fr)] lg:border-x lg:border-b">
          {/* Filter card — left column; sticky under nav on desktop (WEB-312). */}
          <aside className="relative lg:p-0">
            <BlogFilterRail
              filters={filters}
              totalCount={totalCount}
              labels={labels}
            />
          </aside>

          {/* Rows column — full-height left divider (Figma 4392:12102). */}
          <div className="border-grey-100 relative lg:border-l">
            <BlogAccordionList posts={visiblePosts} labels={labels} />

            {filteredPosts.length === 0 ? (
              <div className="px-10 py-16 text-center">
                <p className="text-ds-body-md text-grey-500">
                  {labels.emptyState}
                </p>
              </div>
            ) : null}
          </div>
        </div>

        {hasMore ? (
          <div className="flex justify-center pt-10">
            <button
              type="button"
              onClick={() =>
                setVisibleCount((count) => count + POSTS_PER_BATCH)
              }
              className="bg-grey-0 border-grey-100 text-ds-label-md text-grey-950 rounded-ds-full hover:bg-grey-50 inline-flex cursor-pointer items-center border px-4 py-2 font-medium transition-colors"
            >
              {labels.loadMore}
            </button>
          </div>
        ) : null}

        {/* Breathing room before the footer banner (Figma token 13 = 80px). */}
        <div aria-hidden className="h-20" />
      </div>
    </div>
  );
}
